import * as Blockly from 'blockly/core';
import { BOARD_CONFIG, type BoardType } from '@/(routers)/boards/editor/block/blockConfig';

export function stmt(line: string): string {
    return `${line};\n`;
}

export function stmts(lines: string[]): string {
    return lines.map((l) => `${l};\n`).join('');
}

export function expr(code: string, precedence = 0): [string, number] {
    return [code, precedence];
}

export function blockIf(cond: string, body: string): string {
    return `if (${cond}) {\n${body}}\n`;
}

export function blockElseIf(cond: string, body: string): string {
    return ` else if (${cond}) {\n${body}}`;
}

export function blockElse(body: string): string {
    return ` else {\n${body}}`;
}

export function blockWhile(cond: string, body: string): string {
    return `while (${cond}) {\n${body}}\n`;
}

export function blockFor(init: string, cond: string, step: string, body: string): string {
    return `for (${init}; ${cond}; ${step}) {\n${body}}\n`;
}

export function indent(code: string, level = 1): string {
    const pad = '  '.repeat(level);
    return code
        .split('\n')
        .map((line) => (line ? pad + line : line))
        .join('\n');
}

export function resolveBoard(block: Blockly.Block): BoardType {
    const workspace = block.workspace;
    const board = workspace.getBlocksByType('schoomy_board')[0]?.getFieldValue('BOARD');
    if (board !== 'blue' && board !== 'orange') {
        throw new Error(`Board block is missing or invalid: BOARD=${String(board)}`);
    }
    return board;
}

export function resolveDigitalPin(block: Blockly.Block): string {
    type Position = keyof (typeof BOARD_CONFIG)[BoardType]['pins'];
    const position = block.getFieldValue('POSITION') as Position;
    const board = resolveBoard(block);
    const config = BOARD_CONFIG[board];
    const pin = config.pins[position as keyof typeof config.pins]?.second;
    if (pin === undefined) {
        throw new Error(`Unsupported digital position: board=${board}, position=${String(position)}`);
    }
    return String(pin);
}

export function resolveAnalogPin(block: Blockly.Block): {
    pin: string;
    config: (typeof BOARD_CONFIG)[BoardType];
} {
    type AnalogPosition = keyof (typeof BOARD_CONFIG)[BoardType]['analogPins'];
    const position = block.getFieldValue('POSITION') as AnalogPosition;
    const board = resolveBoard(block);
    const config = BOARD_CONFIG[board];
    const pin = config.analogPins[position as keyof typeof config.analogPins];
    if (!pin) {
        throw new Error(`Unsupported analog position: board=${board}, position=${String(position)}`);
    }
    return { pin, config };
}

export function resolveI2CPins(block: Blockly.Block): {
    sda: string;
    scl: string;
    board: BoardType;
    config: (typeof BOARD_CONFIG)[BoardType];
} {
    type Position = keyof (typeof BOARD_CONFIG)[BoardType]['pins'];
    const position = block.getFieldValue('POSITION') as Position;
    const board = resolveBoard(block);
    const config = BOARD_CONFIG[board];
    const pins = config.pins[position as keyof typeof config.pins];
    if (!pins) {
        throw new Error(`Unsupported I2C position: board=${board}, position=${String(position)}`);
    }
    return { sda: String(pins.first), scl: String(pins.second), board, config };
}

const _setupCodes = new Map<string, Set<string>>();

export function registerSetupCode(key: string, code: string): void {
    const codes = _setupCodes.get(key) ?? new Set<string>();
    codes.add(code);
    _setupCodes.set(key, codes);
}

export function getSetupCodes(): IterableIterator<string> {
    return [..._setupCodes.values()].flatMap((codes) => [...codes]).values();
}

export function resetSetupCodes(): void {
    _setupCodes.clear();
}

// setupCodeと同じ仕組みで、ユーザーがloopブロックの中に何を置いたかに関係なく、
// 毎ループ確実に実行したいコード(例: Bluetooth接続デバイス名の定期再送)を登録する。
const _loopCodes = new Map<string, Set<string>>();

export function registerLoopCode(key: string, code: string): void {
    const codes = _loopCodes.get(key) ?? new Set<string>();
    codes.add(code);
    _loopCodes.set(key, codes);
}

export function getLoopCodes(): IterableIterator<string> {
    return [..._loopCodes.values()].flatMap((codes) => [...codes]).values();
}

export function resetLoopCodes(): void {
    _loopCodes.clear();
}

const _helperFunctions = new Map<string, string>();

export function registerHelperFunction(key: string, code: string): void {
    _helperFunctions.set(key, code);
}

export function getHelperFunctions(): string {
    return [..._helperFunctions.values()].join('\n');
}

export function resetHelperFunctions(): void {
    _helperFunctions.clear();
}

let _wireBeginCode: string | null = null;

export function registerWireBegin(code: string): void {
    if (_wireBeginCode === null) {
        _wireBeginCode = code;
    }
}

export function getWireBeginCode(): string {
    return _wireBeginCode ?? '';
}

export function resetWireBegin(): void {
    _wireBeginCode = null;
}
